import { useApolloClient } from '@apollo/client';
import { LaborMetricsInterval } from '__generated__/graphql';
import {
  getDefaultDashboardListRow,
  IDashboardListRow,
} from 'components/foundation/dashboard/dashboard-list/DashboardList/DashboardList';
import {
  PROJECT_WITH_TIME_AND_COST_CODE_TIME_ONLY_METRIC_AND_SUB_CHECK_QUERY,
  PROJECT_WITH_TIME_AND_EQUIPMENT_TIME_ONLY_METRIC_AND_SUB_CHECK_QUERY,
  PROJECT_WITH_TIME_AND_MEMBER_TIME_ONLY_METRIC_AND_SUB_CHECK_QUERY,
} from 'containers/activity-reports/queries/project-activity-queries';
import { useDashboardTimeRange } from 'containers/dashboard/hooks';
import { DashboardTimeFrame } from 'containers/dashboard/types/types';
import gql from 'graphql-tag';
import { useApolloPaging, useAsync, useOrganization } from 'hooks';
import { first, isNil, take } from 'lodash';
import { Duration } from 'luxon';
import { useCallback, useEffect, useMemo } from 'react';
import ICursorable from 'types/Cursorable';
import IProject from 'types/Project';
import { mapNotNull, takeWithPopulate } from 'utils/collectionUtils';
import { getMetricLaborTotals } from 'utils/jitMetricUtils';
import { getFormattedPathFromProject } from 'utils/projectUtils';
import { sortByField } from 'utils/tableUtils';

interface IProjectTotal {
  id: string;
  title: string;
  total: number;
}

export const ROOT_PROJECT_LABOR_METRICS_QUERY = gql`
  query ActiveRootProjectLaborMetrics($interval: LaborMetricsInterval!, $start: Date!, $end: Date!, $depth: Int) {
    projectLaborMetrics(interval: $interval, start: $start, end: $end, depth: $depth) {
      projectId
      start
      end
      rt
      ot
      dt
      descRt
      descOt
      descDt
    }
  }
`;

// Title path for just the top projects, by id.
export const PROJECT_TITLE_PATHS_QUERY = gql`
  query ActiveProjectTitlePaths($filter: ProjectFilter) {
    projects(filter: $filter) {
      id
      title
      depth
      ancestors {
        depth
        title
      }
    }
  }
`;

export default function useActiveProjectDashboard(
  timeFrame: DashboardTimeFrame,
  memberId?: string | null,
  costCodeId?: string | null,
  equipmentId?: string | null,
  includeAddOns?: boolean | null
) {
  const timeRange = useDashboardTimeRange(timeFrame, 'system');
  const organization = useOrganization();
  const { getAll } = useApolloPaging();
  const client = useApolloClient();

  const isEntityFiltered = !isNil(memberId) || !isNil(costCodeId) || !isNil(equipmentId);

  const getRootProjectTotals = useCallback(async (): Promise<IProjectTotal[]> => {
    const metricsResult = await client.query<{ projectLaborMetrics: Array<{ projectId: string | null }> }>({
      query: ROOT_PROJECT_LABOR_METRICS_QUERY,
      fetchPolicy: 'network-only',
      variables: {
        interval: LaborMetricsInterval.Custom,
        start: timeRange.startTime.toISODate(),
        end: timeRange.endTime.toISODate(),
        depth: 1,
      },
    });

    const totals = mapNotNull(metricsResult.data.projectLaborMetrics, (metric) => {
      const total = getMetricLaborTotals(metric as any)?.totalSeconds ?? 0;
      return total > 0 && !isNil(metric.projectId) ? { projectId: metric.projectId, total } : null;
    });

    const top = take(sortByField(totals, 'total', 'DESC'), 5);
    if (top.length === 0) {
      return [];
    }

    const ids = top.map((item) => item.projectId);
    const projectsResult = await client.query<{ projects: IProject[] }>({
      query: PROJECT_TITLE_PATHS_QUERY,
      fetchPolicy: 'network-only',
      variables: { filter: { id: { contains: ids } } },
    });
    const pathById = new Map(
      projectsResult.data.projects.map((project) => [
        project.id,
        getFormattedPathFromProject(project, true) || project.title,
      ])
    );

    return top.map((item) => ({ id: item.projectId, title: pathById.get(item.projectId) ?? '', total: item.total }));
  }, [client, timeRange.startTime, timeRange.endTime]);

  // Entity-filtered (landing page) path: unchanged — page projectsWithTime with the per-entity metric.
  const getFilteredProjectTotals = useCallback(async (): Promise<IProjectTotal[]> => {
    let query = PROJECT_WITH_TIME_AND_MEMBER_TIME_ONLY_METRIC_AND_SUB_CHECK_QUERY;
    if (!isNil(costCodeId)) {
      query = PROJECT_WITH_TIME_AND_COST_CODE_TIME_ONLY_METRIC_AND_SUB_CHECK_QUERY;
    } else if (!isNil(equipmentId)) {
      query = PROJECT_WITH_TIME_AND_EQUIPMENT_TIME_ONLY_METRIC_AND_SUB_CHECK_QUERY;
    }

    const isAddOnIncluded = !!includeAddOns;
    const projects = await getAll<IProject & ICursorable>('projectsWithTime', {
      query,
      fetchPolicy: 'network-only',
      variables: {
        first: 200,
        filter: { depth: { equal: 1 }, archivedOn: { isNull: true } },
        sort: [{ title: 'asc' }],
        metricsStartDate: timeRange.startTime.toISODate(),
        metricsEndDate: timeRange.endTime.toISODate(),
        startTime: timeRange.startTime.toISO({ suppressMilliseconds: true, includeOffset: false }),
        endTime: timeRange.endTime.toISO({ suppressMilliseconds: true, includeOffset: false }),
        includeOpenEntry: false,
        includeAncestors: true,
        metricsInterval: LaborMetricsInterval.Custom,
        memberId: !isNil(memberId) ? { equal: memberId } : undefined,
        memberIds: !isNil(memberId) ? [memberId] : undefined,
        costCodeId: !isNil(costCodeId) ? { equal: costCodeId } : undefined,
        costCodeIds: !isNil(costCodeId) ? [costCodeId] : undefined,
        equipmentId: isAddOnIncluded ? undefined : !isNil(equipmentId) ? { equal: equipmentId } : undefined,
        equipmentOrAddOnId: isAddOnIncluded ? (!isNil(equipmentId) ? { equal: equipmentId } : undefined) : undefined,
        equipmentIds: isAddOnIncluded ? undefined : !isNil(equipmentId) ? [equipmentId] : undefined,
      },
    });

    return mapNotNull(projects, (project) => {
      let total = 0;
      if (!isNil(memberId)) {
        total = getMetricLaborTotals(first(project.projectMemberLaborMetrics))?.totalSeconds ?? 0;
      } else if (!isNil(costCodeId)) {
        total = getMetricLaborTotals(first(project.projectCostCodeLaborMetrics))?.totalSeconds ?? 0;
      } else if (!isNil(equipmentId)) {
        total = getMetricLaborTotals(first(project.projectEquipmentLaborMetrics))?.totalSeconds ?? 0;
      }
      return total > 0 ? { id: project.id, title: project.title, total } : null;
    });
  }, [memberId, costCodeId, equipmentId, includeAddOns, getAll, timeRange.startTime, timeRange.endTime]);

  const getData = useCallback(
    () => (isEntityFiltered ? getFilteredProjectTotals() : getRootProjectTotals()),
    [isEntityFiltered, getFilteredProjectTotals, getRootProjectTotals]
  );

  const { execute, loading, error, data } = useAsync(getData, false);

  const rowData: IDashboardListRow[] = useMemo(() => {
    const activeProjects = take(sortByField(data ?? [], 'total', 'DESC'), 5).map((item) => ({
      value: Duration.fromMillis(item.total * 1000).toFormat('h:mm'),
      label: item.title,
      id: item.id,
    }));

    return takeWithPopulate(activeProjects, 5, getDefaultDashboardListRow);
  }, [data]);

  useEffect(() => {
    if (organization.trackProject) {
      execute();
    }
  }, [timeFrame, organization.trackProject, execute, memberId, costCodeId, equipmentId]);

  return {
    data: rowData,
    loading,
    error,
    execute,
  };
}
